import { download, gql, removeRest, upload } from './client'
import {
  ApprovalListItem,
  ApprovalTab,
  AppSettings,
  Attachment,
  CalendarPreview,
  CatalogItem,
  CatalogKind,
  CrewOverview,
  EquipmentAsset,
  EquipmentAssetInput,
  Invitation,
  Issue,
  IssueInput,
  Material,
  MaterialInput,
  Me,
  Member,
  MemberInput,
  NotificationItem,
  PortfolioAnalytics,
  Project,
  ProjectDocument,
  ProjectInput,
  ProjectRole,
  ProjectSummary,
  Report,
  ReportContent,
  PortfolioReport,
  ReportListItem,
  Section,
  Site,
  SiteInput,
  Subcontractor,
  SubcontractorInput,
  TemplateInfo,
  User,
} from './types'

const APP_SETTINGS = 'organisationName reminderTime escalationTime remindOnFridays sessionTimeoutHours defaultTheme defaultDensity maintenanceMode defaultLocale exportLocale dateFormat timeFormat units currency latinDigits'
const USER = 'id name email isAdmin isActive lastLoginAt createdAt'
const ME = `${USER} memberships { projectId projectName role }`
const PROJECT = 'id name tenderNumber client contractor consultant location latitude longitude startDate endDate status myRole'
const CATALOG = 'id kind label sortOrder isFixed'
const EQUIPMENT_ASSET =
  'id code name model type ownership rentalVendor dailyRate status currentSiteId currentSiteName currentProjectId currentProjectName hoursThisMonth'
const MATERIAL = 'id code name unit category defaultSupplier notes usedThisMonth usedThisMonthUnit deliveriesThisMonth'
const SITE = 'id projectId projectName name scope engineerId engineerName shift status'
const SUBCONTRACTOR = 'id name contactName phone trades status sitesToday workersToday'
const ISSUE = 'id projectId title description severity status reportedByName resolvedByName createdAt resolvedAt'
const PROJECT_DOCUMENT = 'id projectId originalName mimeType size uploadedByName createdAt'
const CREW_OVERVIEW = `
  workersToday workersYesterday tradesCount activeSitesToday
  subcontractorsActive subcontractorsOnHold subcontractorsEnded
  avgWorkersPerDay30d avgWorkersPerDay30dPrev
  trades { title subcontractorCount sitesCount workersToday avgPerDay30d shareOfCrewPct }
  subcontractors { ${SUBCONTRACTOR} }
`
const ATTACHMENT =
  'id reportId section lineId caption originalName mimeType size uploadedByName createdAt reportCode reportDate'
const REPORT = `
  id projectId code sequence reportDate dayName weather workflow status
  project { ${PROJECT} }
  staff { id title count name subcontractorId }
  labTests { id testType location result }
  materials { id name quantity }
  equipment { id name count }
  works { id text }
  visits { id party reason minutes }
  preparedByName lastEditedByName lastEditedAt submittedByName submittedAt
  approvedByName approvedAt returnedByName returnedAt returnNote myRole
  permissions { canView canEdit canSubmit canApprove canReturn canReopen canAddNote }
  attachments { ${ATTACHMENT} }
  notes { id text authorId authorName createdAt section }
  audit { id action note actorName createdAt }
`
const LIST_ITEM =
  'id code sequence reportDate dayName status workflow weather fillRatio filledSections attachmentCount notesCount preparedByName submittedAt'

export const login = (email: string, password: string) =>
  gql<{ login: { token: string; user: Me } }>(
    `mutation($email: String!, $password: String!) { login(email: $email, password: $password) { token user { ${ME} } } }`,
    { email, password }
  ).then((data) => data.login)

export const fetchMe = () => gql<{ me: Me }>(`{ me { ${ME} } }`).then((data) => data.me)

export const requestPasswordReset = (email: string) =>
  gql<{ requestPasswordReset: boolean }>(
    'mutation($email: String!) { requestPasswordReset(email: $email) }',
    { email }
  ).then((data) => data.requestPasswordReset)

export const verifyOtp = (code: string) =>
  gql<{ verifyOtp: { token: string; user: Me } }>(
    `mutation($code: String!) { verifyOtp(code: $code) { token user { ${ME} } } }`,
    { code }
  ).then((data) => data.verifyOtp)

const INVITATION = 'id token email projectId projectName role invitedByName invitedByRole expiresAt'

export const fetchInvitation = (token: string) =>
  gql<{ invitation: Invitation }>(`query($token: String!) { invitation(token: $token) { ${INVITATION} } }`, { token }).then(
    (data) => data.invitation
  )

export const acceptInvitation = (token: string, name: string, password: string) =>
  gql<{ acceptInvitation: { token: string; user: Me } }>(
    `mutation($token: String!, $name: String!, $password: String!) {
      acceptInvitation(token: $token, name: $name, password: $password) { token user { ${ME} } }
    }`,
    { name, password, token }
  ).then((data) => data.acceptInvitation)

export const fetchToday = () => gql<{ today: string }>('{ today }').then((data) => data.today)

export const fetchProjects = () =>
  gql<{ projects: Project[] }>(`{ projects { ${PROJECT} } }`).then((data) => data.projects)

export const fetchProject = (id: string) =>
  gql<{ project: Project }>(`query($id: ID!) { project(id: $id) { ${PROJECT} } }`, { id }).then(
    (data) => data.project
  )

export const createProject = (input: ProjectInput, members: MemberInput[] = []) =>
  gql<{ createProject: Project }>(
    `mutation($input: ProjectInput!, $members: [MemberInput!]) { createProject(input: $input, members: $members) { ${PROJECT} } }`,
    { input, members }
  ).then((data) => data.createProject)

export const fetchSites = (projectId: string) =>
  gql<{ sites: Site[] }>(`query($projectId: ID!) { sites(projectId: $projectId) { ${SITE} } }`, { projectId }).then(
    (data) => data.sites
  )

export const fetchAllSites = () => gql<{ allSites: Site[] }>(`{ allSites { ${SITE} } }`).then((data) => data.allSites)

export const createSite = (projectId: string, input: SiteInput) =>
  gql<{ createSite: Site }>(
    `mutation($projectId: ID!, $input: SiteInput!) { createSite(projectId: $projectId, input: $input) { ${SITE} } }`,
    { input, projectId }
  ).then((data) => data.createSite)

export const updateSite = (id: string, input: SiteInput) =>
  gql<{ updateSite: Site }>(`mutation($id: ID!, $input: SiteInput!) { updateSite(id: $id, input: $input) { ${SITE} } }`, {
    id,
    input,
  }).then((data) => data.updateSite)

export const removeSite = (id: string) =>
  gql<{ removeSite: boolean }>('mutation($id: ID!) { removeSite(id: $id) }', { id }).then((data) => data.removeSite)

export const fetchCalendarPreview = (startDate: string, endDate: string) =>
  gql<{ projectCalendarPreview: CalendarPreview }>(
    `query($startDate: String!, $endDate: String!) {
      projectCalendarPreview(startDate: $startDate, endDate: $endDate) { days firstCode lastCode months { month days firstCode lastCode } }
    }`,
    { endDate, startDate }
  ).then((data) => data.projectCalendarPreview)

export const updateProject = (id: string, input: ProjectInput) =>
  gql<{ updateProject: Project }>(
    `mutation($id: ID!, $input: ProjectInput!) { updateProject(id: $id, input: $input) { ${PROJECT} } }`,
    { id, input }
  ).then((data) => data.updateProject)

export const fetchCatalog = (projectId: string) =>
  gql<{ catalog: CatalogItem[] }>(`query($projectId: ID!) { catalog(projectId: $projectId) { ${CATALOG} } }`, {
    projectId,
  }).then((data) => data.catalog)

export const addCatalogItem = (projectId: string, kind: CatalogKind, label: string, isFixed = false) =>
  gql<{ addCatalogItem: CatalogItem }>(
    `mutation($projectId: ID!, $kind: CatalogKind!, $label: String!, $isFixed: Boolean) {
      addCatalogItem(projectId: $projectId, kind: $kind, label: $label, isFixed: $isFixed) { ${CATALOG} }
    }`,
    { isFixed, kind, label, projectId }
  ).then((data) => data.addCatalogItem)

export const updateCatalogItem = (id: string, patch: { label?: string; isFixed?: boolean; sortOrder?: number }) =>
  gql<{ updateCatalogItem: CatalogItem }>(
    `mutation($id: ID!, $label: String, $isFixed: Boolean, $sortOrder: Int) {
      updateCatalogItem(id: $id, label: $label, isFixed: $isFixed, sortOrder: $sortOrder) { ${CATALOG} }
    }`,
    { id, ...patch }
  ).then((data) => data.updateCatalogItem)

export const removeCatalogItem = (id: string) =>
  gql<{ removeCatalogItem: boolean }>('mutation($id: ID!) { removeCatalogItem(id: $id) }', { id })

export const fetchMembers = (projectId: string) =>
  gql<{ members: Member[] }>(
    `query($projectId: ID!) { members(projectId: $projectId) { id role user { ${USER} } } }`,
    { projectId }
  ).then((data) => data.members)

export const setMember = (projectId: string, userId: string, role: ProjectRole) =>
  gql<{ setMember: boolean }>(
    'mutation($projectId: ID!, $userId: ID!, $role: ProjectRole!) { setMember(projectId: $projectId, userId: $userId, role: $role) }',
    { projectId, role, userId }
  )

export const removeMember = (projectId: string, userId: string) =>
  gql<{ removeMember: boolean }>(
    'mutation($projectId: ID!, $userId: ID!) { removeMember(projectId: $projectId, userId: $userId) }',
    { projectId, userId }
  )

export const fetchInvitations = (projectId: string) =>
  gql<{ invitations: Invitation[] }>(
    `query($projectId: ID!) { invitations(projectId: $projectId) { ${INVITATION} } }`,
    { projectId }
  ).then((data) => data.invitations)

export const createInvitation = (projectId: string, email: string, role: ProjectRole) =>
  gql<{ createInvitation: Invitation }>(
    `mutation($projectId: ID!, $email: String!, $role: ProjectRole!) {
      createInvitation(projectId: $projectId, email: $email, role: $role) { ${INVITATION} }
    }`,
    { email, projectId, role }
  ).then((data) => data.createInvitation)

export const revokeInvitation = (id: string) =>
  gql<{ revokeInvitation: boolean }>('mutation($id: ID!) { revokeInvitation(id: $id) }', { id })

export const fetchAllInvitations = () =>
  gql<{ allInvitations: Invitation[] }>(`{ allInvitations { ${INVITATION} } }`).then((data) => data.allInvitations)

export const fetchEquipmentAssets = () =>
  gql<{ equipmentAssets: EquipmentAsset[] }>(`{ equipmentAssets { ${EQUIPMENT_ASSET} } }`).then((data) => data.equipmentAssets)

export const createEquipmentAsset = (input: EquipmentAssetInput) =>
  gql<{ createEquipmentAsset: EquipmentAsset }>(
    `mutation($input: EquipmentAssetInput!) { createEquipmentAsset(input: $input) { ${EQUIPMENT_ASSET} } }`,
    { input }
  ).then((data) => data.createEquipmentAsset)

export const updateEquipmentAsset = (id: string, input: EquipmentAssetInput) =>
  gql<{ updateEquipmentAsset: EquipmentAsset }>(
    `mutation($id: ID!, $input: EquipmentAssetInput!) { updateEquipmentAsset(id: $id, input: $input) { ${EQUIPMENT_ASSET} } }`,
    { id, input }
  ).then((data) => data.updateEquipmentAsset)

export const removeEquipmentAsset = (id: string) =>
  gql<{ removeEquipmentAsset: boolean }>('mutation($id: ID!) { removeEquipmentAsset(id: $id) }', { id }).then(
    (data) => data.removeEquipmentAsset
  )

export const fetchMaterials = () => gql<{ materials: Material[] }>(`{ materials { ${MATERIAL} } }`).then((data) => data.materials)

export const createMaterial = (input: MaterialInput) =>
  gql<{ createMaterial: Material }>(`mutation($input: MaterialInput!) { createMaterial(input: $input) { ${MATERIAL} } }`, {
    input,
  }).then((data) => data.createMaterial)

export const updateMaterial = (id: string, input: MaterialInput) =>
  gql<{ updateMaterial: Material }>(
    `mutation($id: ID!, $input: MaterialInput!) { updateMaterial(id: $id, input: $input) { ${MATERIAL} } }`,
    { id, input }
  ).then((data) => data.updateMaterial)

export const removeMaterial = (id: string) =>
  gql<{ removeMaterial: boolean }>('mutation($id: ID!) { removeMaterial(id: $id) }', { id }).then((data) => data.removeMaterial)

export const fetchPortfolioAnalytics = (from: string, to: string, projectId?: string) =>
  gql<{ portfolioAnalytics: PortfolioAnalytics }>(
    `query($from: String!, $to: String!, $projectId: ID) {
      portfolioAnalytics(from: $from, to: $to, projectId: $projectId) {
        from to reportsPerDay { date value } reportsSubmittedTotal reportsSubmittedDeltaPct
        manpowerPerDay { date value } manpowerAvg manpowerDeltaPct
        equipmentUsage { name unitDays } equipmentDeltaPct
        onTimeRate onTimeCount dueCount onTimeDeltaPts
        statusSplit { status count } totalReports
      }
    }`,
    { from, projectId, to }
  ).then((data) => data.portfolioAnalytics)

export const fetchCrewOverview = () => gql<{ crewOverview: CrewOverview }>(`{ crewOverview { ${CREW_OVERVIEW} } }`).then((data) => data.crewOverview)

export const fetchSubcontractors = () =>
  gql<{ subcontractors: Subcontractor[] }>(`{ subcontractors { ${SUBCONTRACTOR} } }`).then((data) => data.subcontractors)

export const createSubcontractor = (input: SubcontractorInput) =>
  gql<{ createSubcontractor: Subcontractor }>(
    `mutation($input: SubcontractorInput!) { createSubcontractor(input: $input) { ${SUBCONTRACTOR} } }`,
    { input }
  ).then((data) => data.createSubcontractor)

export const updateSubcontractor = (id: string, input: SubcontractorInput) =>
  gql<{ updateSubcontractor: Subcontractor }>(
    `mutation($id: ID!, $input: SubcontractorInput!) { updateSubcontractor(id: $id, input: $input) { ${SUBCONTRACTOR} } }`,
    { id, input }
  ).then((data) => data.updateSubcontractor)

export const removeSubcontractor = (id: string) =>
  gql<{ removeSubcontractor: boolean }>('mutation($id: ID!) { removeSubcontractor(id: $id) }', { id }).then(
    (data) => data.removeSubcontractor
  )

export const fetchUsers = () => gql<{ users: Me[] }>(`{ users { ${ME} } }`).then((data) => data.users)

export const createUser = (input: { name: string; email: string; password: string; isAdmin?: boolean }) =>
  gql<{ createUser: User }>(`mutation($input: CreateUserInput!) { createUser(input: $input) { ${USER} } }`, {
    input,
  }).then((data) => data.createUser)

export const updateUser = (
  id: string,
  input: { name?: string; password?: string; isAdmin?: boolean; isActive?: boolean }
) =>
  gql<{ updateUser: User }>(
    `mutation($id: ID!, $input: UpdateUserInput!) { updateUser(id: $id, input: $input) { ${USER} } }`,
    { id, input }
  ).then((data) => data.updateUser)

export const fetchPortfolioReports = (from?: string, to?: string) =>
  gql<{ portfolioReports: PortfolioReport[] }>(
    `query($from: String, $to: String) { portfolioReports(from: $from, to: $to) { ${LIST_ITEM} projectId projectName } }`,
    { from, to }
  ).then((data) => data.portfolioReports)

export const fetchReports = (projectId: string, from?: string, to?: string) =>
  gql<{ reports: ReportListItem[] }>(
    `query($projectId: ID!, $from: String, $to: String) { reports(projectId: $projectId, from: $from, to: $to) { ${LIST_ITEM} } }`,
    { from, projectId, to }
  ).then((data) => data.reports)

export const fetchReport = (id: string) =>
  gql<{ report: Report }>(`query($id: ID!) { report(id: $id) { ${REPORT} } }`, { id }).then((data) => data.report)

export const findReportByDate = (projectId: string, date: string) =>
  gql<{ reportIdByDate: string | null }>(
    'query($projectId: ID!, $date: String!) { reportIdByDate(projectId: $projectId, date: $date) }',
    { date, projectId }
  ).then((data) => data.reportIdByDate)

export const saveReport = (id: string, input: ReportContent) =>
  gql<{ saveReport: Report }>(
    `mutation($id: ID!, $input: ReportContentInput!) { saveReport(id: $id, input: $input) { ${REPORT} } }`,
    { id, input }
  ).then((data) => data.saveReport)

type Transition = 'submitReport' | 'approveReport'
type NotedTransition = 'returnReport' | 'reopenReport'

export const transitionReport = (mutation: Transition, id: string) =>
  gql<Record<Transition, Report>>(`mutation($id: ID!) { ${mutation}(id: $id) { ${REPORT} } }`, { id }).then(
    (data) => data[mutation]
  )

export const notedTransition = (mutation: NotedTransition, id: string, note: string) =>
  gql<Record<NotedTransition, Report>>(
    `mutation($id: ID!, $note: String!) { ${mutation}(id: $id, note: $note) { ${REPORT} } }`,
    { id, note }
  ).then((data) => data[mutation])

const APPROVAL_ITEM =
  'id code sequence projectId projectName reportDate dayName status workflow weather attachmentCount notesCount preparedByName submittedAt submittedByName approvedAt returnedAt returnNote staffCount equipmentCount'

export const fetchApprovals = (tab: ApprovalTab, from?: string, to?: string) =>
  gql<{ approvals: ApprovalListItem[] }>(
    `query($tab: ApprovalTab!, $from: String, $to: String) { approvals(tab: $tab, from: $from, to: $to) { ${APPROVAL_ITEM} } }`,
    { from, tab, to }
  ).then((data) => data.approvals)

export const fetchMyReports = (from?: string, to?: string) =>
  gql<{ myReports: ApprovalListItem[] }>(
    `query($from: String, $to: String) { myReports(from: $from, to: $to) { ${APPROVAL_ITEM} } }`,
    { from, to }
  ).then((data) => data.myReports)

export const approveReports = (ids: string[]) =>
  gql<{ approveReports: Report[] }>(`mutation($ids: [ID!]!) { approveReports(ids: $ids) { id } }`, { ids }).then(
    (data) => data.approveReports
  )

export const returnReports = (ids: string[], note: string) =>
  gql<{ returnReports: Report[] }>(
    `mutation($ids: [ID!]!, $note: String!) { returnReports(ids: $ids, note: $note) { id } }`,
    { ids, note }
  ).then((data) => data.returnReports)

export const addSupervisionNote = (reportId: string, text: string, section?: Section) =>
  gql<{ addSupervisionNote: Report }>(
    `mutation($reportId: ID!, $text: String!, $section: Section) { addSupervisionNote(reportId: $reportId, text: $text, section: $section) { ${REPORT} } }`,
    { reportId, section, text }
  ).then((data) => data.addSupervisionNote)

export const deleteSupervisionNote = (id: string) =>
  gql<{ deleteSupervisionNote: Report }>(
    `mutation($id: ID!) { deleteSupervisionNote(id: $id) { ${REPORT} } }`,
    { id }
  ).then((data) => data.deleteSupervisionNote)

export const fetchProjectAttachments = (projectId: string, filters: { section?: string; from?: string; to?: string }) =>
  gql<{ projectAttachments: Attachment[] }>(
    `query($projectId: ID!, $section: String, $from: String, $to: String) {
      projectAttachments(projectId: $projectId, section: $section, from: $from, to: $to) { ${ATTACHMENT} }
    }`,
    { projectId, ...filters }
  ).then((data) => data.projectAttachments)

export const updateAttachment = (id: string, patch: { caption?: string; lineId?: string }) =>
  gql<{ updateAttachment: Attachment }>(
    `mutation($id: ID!, $caption: String, $lineId: String) { updateAttachment(id: $id, caption: $caption, lineId: $lineId) { ${ATTACHMENT} } }`,
    { id, ...patch }
  ).then((data) => data.updateAttachment)

export const deleteAttachment = (id: string) =>
  gql<{ deleteAttachment: boolean }>('mutation($id: ID!) { deleteAttachment(id: $id) }', { id })

export const uploadAttachments = (
  reportId: string,
  section: Section,
  files: File[],
  options: { lineId?: string; caption?: string } = {}
) => {
  const form = new FormData()
  form.append('reportId', reportId)
  form.append('section', section)
  if (options.lineId) form.append('lineId', options.lineId)
  if (options.caption) form.append('caption', options.caption)
  files.forEach((file) => form.append('files', file))
  return upload<{ id: string; originalName: string }[]>('/attachments', form)
}

export const fetchNotifications = () =>
  gql<{ notifications: NotificationItem[] }>(
    '{ notifications { id type readAt createdAt reportId reportCode reportDate projectId projectName } }'
  ).then((data) => data.notifications)

export const markNotificationRead = (id: string) =>
  gql<{ markNotificationRead: boolean }>('mutation($id: ID!) { markNotificationRead(id: $id) }', { id })

export const markAllNotificationsRead = () =>
  gql<{ markAllNotificationsRead: boolean }>('mutation { markAllNotificationsRead }')

export const registerPushToken = (token: string) =>
  gql<{ registerPushToken: boolean }>('mutation($token: String!) { registerPushToken(token: $token) }', { token })

export const unregisterPushToken = (token: string) =>
  gql<{ unregisterPushToken: boolean }>('mutation($token: String!) { unregisterPushToken(token: $token) }', { token })

export const fetchSummary = (projectId: string, from: string, to: string) =>
  gql<{ projectSummary: ProjectSummary }>(
    `query($projectId: ID!, $from: String!, $to: String!) {
      projectSummary(projectId: $projectId, from: $from, to: $to) {
        from to totalDays workingDays visits notes attachments
        statusCounts { status count }
        materials { name deliveries days quantities total unit }
        staff { title personDays days }
        equipment { name unitDays days }
        labTests { testType count }
      }
    }`,
    { from, projectId, to }
  ).then((data) => data.projectSummary)

export const fetchTemplateInfo = () =>
  gql<{ templateInfo: TemplateInfo }>('{ templateInfo { custom updatedAt } }').then((data) => data.templateInfo)

export type ExportFormat = 'xlsx' | 'pdf'

export const downloadReport = (id: string, format: ExportFormat) => download(`/reports/${id}/export?format=${format}`)

export const downloadReportAttachments = (id: string) => download(`/reports/${id}/attachments.zip`)

export const downloadRange = (projectId: string, from: string, to: string, format: ExportFormat) =>
  download(`/projects/${projectId}/export?from=${from}&to=${to}&format=${format}`)

export const downloadAttachment = (id: string) => download(`/attachments/${id}`)

export const downloadTemplate = () => download('/template')

export const uploadTemplate = (file: File) => {
  const form = new FormData()
  form.append('file', file)
  return upload<TemplateInfo>('/template', form)
}

export const resetTemplate = () => removeRest<TemplateInfo>('/template')

export const fetchAppSettings = () => gql<{ appSettings: AppSettings }>(`{ appSettings { ${APP_SETTINGS} } }`).then((data) => data.appSettings)

export const updateAppSettings = (input: AppSettings) =>
  gql<{ updateAppSettings: AppSettings }>(`mutation($input: AppSettingsInput!) { updateAppSettings(input: $input) { ${APP_SETTINGS} } }`, {
    input,
  }).then((data) => data.updateAppSettings)

export const downloadAuditLog = () => download('/audit-log/export')

export const fetchIssues = (projectId: string) =>
  gql<{ issues: Issue[] }>(`query($projectId: ID!) { issues(projectId: $projectId) { ${ISSUE} } }`, { projectId }).then((data) => data.issues)

export const createIssue = (projectId: string, input: IssueInput) =>
  gql<{ createIssue: Issue }>(`mutation($projectId: ID!, $input: IssueInput!) { createIssue(projectId: $projectId, input: $input) { ${ISSUE} } }`, {
    input,
    projectId,
  }).then((data) => data.createIssue)

export const resolveIssue = (id: string) =>
  gql<{ resolveIssue: Issue }>(`mutation($id: ID!) { resolveIssue(id: $id) { ${ISSUE} } }`, { id }).then((data) => data.resolveIssue)

export const reopenIssue = (id: string) =>
  gql<{ reopenIssue: Issue }>(`mutation($id: ID!) { reopenIssue(id: $id) { ${ISSUE} } }`, { id }).then((data) => data.reopenIssue)

export const removeIssue = (id: string) => gql<{ removeIssue: boolean }>('mutation($id: ID!) { removeIssue(id: $id) }', { id })

export const fetchDocuments = (projectId: string) =>
  gql<{ documents: ProjectDocument[] }>(`query($projectId: ID!) { documents(projectId: $projectId) { ${PROJECT_DOCUMENT} } }`, { projectId }).then(
    (data) => data.documents
  )

export const uploadDocuments = (projectId: string, files: File[]) => {
  const form = new FormData()
  files.forEach((file) => form.append('files', file))
  return upload<{ id: string; originalName: string }[]>(`/projects/${projectId}/documents`, form)
}

export const downloadDocument = (id: string) => download(`/documents/${id}`)

export const removeDocument = (id: string) => gql<{ removeDocument: boolean }>('mutation($id: ID!) { removeDocument(id: $id) }', { id })
